'use client';

import { useCallback, useRef, useState } from 'react';
import axios from 'axios';

import { fanficsCatalogApi } from '@/lib/fanfics-catalog-api';
import type {
  CatalogFilterFacet,
  CatalogFilterLockedFacets,
  CatalogFilterPageKind,
} from '../types';
import {
  buildCatalogFilterFetchPlan,
  type CatalogFilterFetchMode,
  type FanficsFilterOptions,
  fetchFanficsFilterOptions,
  getLoadingFacets,
  getSkippedFacets,
  mergeCatalogFilterOptions,
} from '@/features/seo-listing/utils';

interface UseCatalogFilterOptionsParams {
  type: 'fanfic' | 'original';
  initialOptions: FanficsFilterOptions;
}

interface ReloadCatalogFilterOptionsParams {
  pageKind: CatalogFilterPageKind;
  lockedFacets: CatalogFilterLockedFacets;
  selectedCharacterIds?: string[];
  selectedTagIds?: string[];
  fetchMode: CatalogFilterFetchMode;
}

interface UseCatalogFilterOptionsResult {
  options: FanficsFilterOptions;
  isLoading: boolean;
  loadingFacets: CatalogFilterFacet[];
  reload: (params: ReloadCatalogFilterOptionsParams) => Promise<void>;
}

export const useCatalogFilterOptions = ({
  type,
  initialOptions,
}: UseCatalogFilterOptionsParams): UseCatalogFilterOptionsResult => {
  const [options, setOptions] = useState(initialOptions);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingFacets, setLoadingFacets] = useState<CatalogFilterFacet[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  const reload = useCallback(
    async ({
      pageKind,
      lockedFacets,
      selectedCharacterIds,
      selectedTagIds,
      fetchMode,
    }: ReloadCatalogFilterOptionsParams) => {
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const fetchPlan = buildCatalogFilterFetchPlan(pageKind, fetchMode, type);
      const skippedFacets = getSkippedFacets(fetchPlan);
      setLoadingFacets(getLoadingFacets(fetchPlan));
      setIsLoading(true);

      try {
        const nextOptions = await fetchFanficsFilterOptions({
          serverApi: fanficsCatalogApi,
          type,
          pageKind,
          fetchMode,
          lockedFacets,
          selectedCharacterIds,
          selectedTagIds,
          signal: controller.signal,
        });

        if (controller.signal.aborted) return;

        setOptions((previous) => mergeCatalogFilterOptions(previous, nextOptions, skippedFacets));
      } catch (error) {
        if (axios.isCancel(error) || controller.signal.aborted) return;
        console.error('[useCatalogFilterOptions] failed to reload filters:', error);
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
          setLoadingFacets([]);
        }
      }
    },
    [type],
  );

  return { options, isLoading, loadingFacets, reload };
};
